import * as React from 'react';

// @ts-expect-error TS7016 Untyped import http://go.dkandu.me/strict-ts-migration#TS7016
import isValidCnpj from '@brazilian-utils/is-valid-cnpj';
// @ts-expect-error TS7016 Untyped import http://go.dkandu.me/strict-ts-migration#TS7016
import isValidCpf from '@brazilian-utils/is-valid-cpf';
import * as Sentry from '@sentry/react';
import moment from 'moment';

import CC, { formatAsNumber } from 'js/lib/creditcard.util';

import { color } from '@coursera/coursera-ui';
import { SvgError } from '@coursera/coursera-ui/svg';

export const InvalidInputIcon = ({ hasError }: { hasError?: boolean }) => (
  <span role="presentation">{hasError && <SvgError color={color.error} size={14} />}</span>
);

const validPostalCodeBR = /^\d{5}[- ]?\d{3}$/; // 5 digits, optional hyphen or space, 3 digits
const validPostalCodeMX = /^\d{5}$/; // 5 digits
const validPostalCodeCO = /^\d{6}$/; // 6 digits

/* 
  1 character, 1 digit, 1 character, space, 1 digit, 1 character, 1 digit, in that order (excluding D,F,I,O,Q,U and also excluding W, Z on the first character position)
  Generally, D,F,I,O,Q,U never appear in CA postal codes, presumably because of their visual similarity to 0, E, 1, 0, 0, and V respectively: http://www.columbia.edu/kermit/postal-ca.html
*/
export const validPostalCodeCA = /^(?![DFIOQUWZ])[A-Z][0-9](?![DFIOQU])[A-Z] ?[0-9](?![DFIOQU])[A-Z][0-9]/i;

const validPhoneLengthForCountry: { [country: string]: { min: number; max: number } } = {
  BR: {
    min: 8,
    max: 13,
  },
  MX: {
    min: 8,
    max: 13,
  },
  CO: {
    min: 7,
    max: 12,
  },
  ID: {
    min: 9,
    max: 12,
  },
  PH: {
    min: 9,
    max: 11,
  },
};

export const getWordCount = (text: string): number => {
  const val = text.trim();
  return val === '' ? 0 : val.split(/\s+/).length;
};

const exported = {
  InvalidInputIcon,

  hasNumOfWords(countLimit: number) {
    return function (val: string): boolean {
      return getWordCount(val) >= countLimit;
    };
  },

  validateNumChars(minCharCount: number, maxCharCount: number) {
    return function (val: string): boolean {
      const strLen = val.replace(/ /g, '').length;
      return strLen >= minCharCount && strLen <= maxCharCount;
    };
  },

  isValidCVV(val: string) {
    return /^\d{3,4}$/.test(val);
  },

  isValidAmexCVV(val: string) {
    return /^\d{4}$/.test(val);
  },

  isValidZipcode(val: string) {
    return /^\d{5}$/.test(val);
  },

  isValidCCNumber(val: string) {
    return CC.isValid(val);
  },

  isValidZipcodePure(val: string) {
    return /^\d{5}$/.test(val);
  },

  isValidCVVPure(val: string) {
    return /^\d{3,4}$/.test(val);
  },

  isValidCCNumberPure(val: string) {
    return CC.isValid(val);
  },

  isValidPostalCode(postalCode: string, country: string): boolean {
    switch (country.toUpperCase()) {
      case 'BR':
        return validPostalCodeBR.test(postalCode);
      case 'MX':
        return validPostalCodeMX.test(postalCode);
      case 'CO':
        return validPostalCodeCO.test(postalCode);
      case 'CA':
        return validPostalCodeCA.test(postalCode);
      default:
        Sentry.captureMessage(`Unsupported country for isValidPostalCode: ${country}`, {
          level: 'warning',
        });
        return false;
    }
  },

  isValidPhoneNumber(phoneNumber: string, country: string): boolean {
    const validLength = validPhoneLengthForCountry[country.toUpperCase()];
    if (!validLength) {
      Sentry.captureMessage(`Unsupported country for isValidPhoneNumber: ${country}`, {
        level: 'warning',
      });
      return false;
    }
    const numberLength = formatAsNumber(phoneNumber).length;
    return numberLength >= validLength.min && numberLength <= validLength.max;
  },

  isValidDocument(document: string, country: string): boolean {
    if (country.toUpperCase() === 'BR') {
      return isValidCpf(document) || isValidCnpj(document);
    } else if (country.toUpperCase() === 'CO') {
      const digits = document.replace(/\D/g, '');
      return digits.length > 1 && digits.length < 11;
    } else if (country.toUpperCase() === 'ID') {
      // NIK: 16 digits per dLocal docs
      const digits = document.replace(/\D/g, '');
      return digits.length === 16;
    } else if (country.toUpperCase() === 'MX') {
      // CURP: 10-18 characters, RFC: 12-13 characters
      const alphanumeric = document.replace(/\s/g, '');
      return /^[A-Z0-9]{10,18}$/i.test(alphanumeric);
    } else if (country.toUpperCase() === 'PH') {
      const digits = document.replace(/\D/g, '');
      return digits.length === 12;
    } else {
      Sentry.captureMessage(`Unsupported country for isValidDocument: ${country}`, {
        level: 'warning',
      });
      return false;
    }
  },

  isValidExpirationDate({ month, year }: { month: string; year: string }): boolean {
    // months are 0-indexed in moment
    const momentObj = moment({ month: parseInt(month, 10) - 1, year: parseInt(year, 10) });
    const momentLastMonthObj = moment().subtract(1, 'month');
    return momentObj.isValid() && momentObj.isAfter(momentLastMonthObj);
  },
};

export default exported;

export const {
  hasNumOfWords,
  validateNumChars,
  isValidCVV,
  isValidAmexCVV,
  isValidZipcode,
  isValidCCNumber,
  isValidZipcodePure,
  isValidCVVPure,
  isValidCCNumberPure,
  isValidPostalCode,
  isValidPhoneNumber,
  isValidDocument,
  isValidExpirationDate,
} = exported;
